import { action, computed, makeObservable, observable } from 'mobx';

export type PreferredLoginMethod = 'magic-link' | 'password';

const PREFERRED_LOGIN_METHOD_KEY = 'auth.preferredLoginMethod';

/**
 * Remembers which login method this browser last signed in with (magic link vs
 * password) so the login screen can default to it on return.
 *
 * Deliberately **device-level, not account-level**: login happens while logged
 * out, so there is no user to attach this to. It lives in localStorage keyed to
 * the browser and survives logout — it is never written to the user's settings.
 *
 * A singleton (mirrors `OmniFilterPreferencesStore`) so both React components
 * and any MobX computeds can read it reactively. `null` means no preference has
 * been recorded yet, in which case the app keeps its existing magic-link default.
 */
export class LoginPreferencesStore {
  @observable
  _preferredMethod: PreferredLoginMethod | null;

  constructor() {
    const stored = localStorage.getItem(PREFERRED_LOGIN_METHOD_KEY);
    this._preferredMethod = stored === 'password' || stored === 'magic-link' ? stored : null;
    makeObservable(this);
  }

  /** The method last used to sign in on this browser, or null if never recorded. */
  @computed
  get preferredMethod(): PreferredLoginMethod | null {
    return this._preferredMethod;
  }

  @action
  setPreferredMethod(method: PreferredLoginMethod) {
    this._preferredMethod = method;
    localStorage.setItem(PREFERRED_LOGIN_METHOD_KEY, method);
  }
}

export const loginPreferences = new LoginPreferencesStore();
